/**
 * Monday move review — the PA manager's exception queue (ruling 2026-10-05).
 *
 * Between 2026-09-18 and the kill of the Monday reassignment job (2026-10-05),
 * Monday moved cases between advocates. Every move was reconciled by the
 * ledger; only the anomalies are here. "Do not manually review every move.
 * Reconcile and exception-review." Each row shows the evidence and offers two
 * decisions, both final:
 *
 *   Accept    keep whoever holds the case now as the baseline;
 *   Reassign  place it with another advocate (the same placement a manager
 *             makes anywhere else, guarded on the holder shown).
 *
 * The server decides who may see this and refuses everyone but a PA manager.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Advocate, freshKey, shortDateTime, STAGE_LABEL, Stage } from './advocateApi';
import {
  mondayMovesApi, MoveQueue, MoveRow, QueuedClass, StatusFilter, QUEUED_CLASSES, CLASS_LABEL, CLASS_WHAT, KIND_LABEL, REASON_LABEL, PLACEMENT_LABEL, Activity,
  MONDAY_USER_BASIS_LABEL, MONDAY_USER_NOT_RECORDED_LABEL, MoveEvidence, BUILDING_SOURCE_LABEL, BUILDING_DECISION_NOTE, BuildingPa, CandidateBuilding,
  ResidentRecords, RESIDENT_UNRESOLVED_LABEL, MERGE_INCONSISTENCY_LABEL,
} from './mondayMovesApi';

/** Who on the board made the move, and how we know; said plainly when it cannot be known. */
const mondayUser = (row: MoveRow, ev: MoveEvidence): string => {
  const se = ev.sourceEvent;
  if (row.monday_user_id) {
    const how = se?.mondayUserBasis ? MONDAY_USER_BASIS_LABEL[se.mondayUserBasis] : undefined;
    return `Monday user ${row.monday_user_id}${how ? ` (${how})` : ''}`;
  }
  const why = se?.mondayUserNotRecorded ? MONDAY_USER_NOT_RECORDED_LABEL[se.mondayUserNotRecorded] || se.mondayUserNotRecorded : 'it was not recorded';
  return `Monday user not recorded: ${why}. The Monday-user identity checks could not run for this move`;
};

const confirmedTag = (c: boolean | null | undefined): string => (c ? ' (confirmed by a manager)' : c === false ? ' (unconfirmed suggestion)' : '');

const nameOr = (p: { name: string | null; user_id: string | null } | null | undefined, none = 'nobody'): string =>
  (p && p.user_id ? p.name || 'a former account' : none);

/** Where a building is on record, in words, each source once. */
const sourcesOf = (b: CandidateBuilding): string =>
  Array.from(new Set(b.sources.map((s) => BUILDING_SOURCE_LABEL[s.source] || s.source))).join(', ');

/** A building's PA at one instant: who, whether a manager confirmed it, and whether it contradicts the move or the holder. */
const paCell = (p: BuildingPa | null, contradicts: boolean): string =>
  (p ? `${nameOr(p)}${p.confirmed && p.confirmed_after_move ? ' (confirmed by a manager after the move)' : confirmedTag(p.confirmed)}${contradicts ? ' · contradicts' : ''}` : 'nobody');

const activity = (a: Activity | undefined): string => {
  if (!a) return '—';
  const parts = [`${a.notes} note${a.notes === 1 ? '' : 's'}`, `${a.statusChanges} status change${a.statusChanges === 1 ? '' : 's'}`, `${a.acts} other act${a.acts === 1 ? '' : 's'}`];
  return parts.join(' · ');
};

/**
 * The resident's records when there is more than the case's own, or when they
 * do not resolve: each record numbered, with its role, the kept record its
 * link and its merge-log entry name, and every way the two disagree.
 */
function ResidentRecordsView({ rr }: { rr: ResidentRecords }) {
  const n = new Map(rr.records.map((r, i) => [r.patient_id, i + 1]));
  const ref = (id: string | null) => (id ? (n.has(id) ? `record ${n.get(id)}` : 'a record not shown') : 'none');
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <>
      {rr.unresolved && <p className="pa-mm-note">{RESIDENT_UNRESOLVED_LABEL[rr.unresolved] || rr.unresolved} Who this resident is must be settled before the move can be.</p>}
      {rr.records.length > 0 && (
        <ol className="pa-mm-records" aria-label="The resident’s records">
          {rr.records.map((r) => (
            <li key={r.patient_id}>
              <strong>{r.name || 'A resident record'}</strong>
              {' · '}{r.role === 'KEPT' ? 'kept' : 'retired'}{r.is_case ? ' · this case’s record' : ''}
              {r.building ? ` · profile: ${r.building}` : ''}
              <span className="pa-mm-quiet"> · linked to {ref(r.merged_into)} · merge log: {r.merge_row_kept ? `kept ${ref(r.merge_row_kept)}` : 'no entry'}</span>
            </li>
          ))}
        </ol>
      )}
      {rr.inconsistencies.length > 0 && (
        <ul className="pa-mm-reasons" aria-label="Where the merge records disagree">
          {rr.inconsistencies.map((x, i) => (
            <li key={`${x.type}-${x.patient_id || i}`}>
              {x.type === 'MORE_THAN_ONE_KEPT'
                ? `Records ${(x.kept_patient_ids || []).map((id) => n.get(id) || '?').join(', ')}: `
                : `${cap(ref(x.patient_id))}: `}
              {MERGE_INCONSISTENCY_LABEL[x.type] || x.type}
              {x.type === 'LINK_MERGES_DISAGREE' ? ` (link: ${ref(x.link_kept)}; merge log: ${ref(x.merge_row_kept)})` : ''}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const tone = (c: string) => (c === 'IDENTITY_CONFLICT' || c === 'NATIVE_ACTION_OVERRIDDEN' ? 'stop' : 'attn');

function MoveCard({ row, advocates, onDone }: { row: MoveRow; advocates: Advocate[]; onDone: (msg: string, reload: boolean) => void }) {
  const [mode, setMode] = useState<'none' | 'accept' | 'reassign'>('none');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const holder = row.holder_now.user_id;
  // The placement service refuses a closed case (409 CASE_CLOSED); Accept is the only decision that can land.
  const closed = row.holder_now.stage === 'CLOSED';
  const ev = row.evidence || {};
  const prior = ev.priorAssignment;
  const later = ev.laterNativePlacement;
  // Every building a record names for the resident (one, when they agree), each with its PA at the move and now.
  const buildings = row.buildings || [];
  const decisionNote = row.building_decision ? BUILDING_DECISION_NOTE[row.building_decision] : null;
  // The resident's merge component, shown when it has more than the case's own record or does not resolve.
  const rr = row.resident_records || null;
  // Advocates a manager confirmed as the PA of a building on record now: offered first-hand in Reassign.
  const confirmedNow = new Map<string, string>();
  for (const b of buildings) if (b.pa_now?.confirmed && !confirmedNow.has(b.pa_now.user_id)) confirmedNow.set(b.pa_now.user_id, b.name || 'a building on record');

  const decide = async () => {
    setBusy(true); setError(null);
    try {
      const r = mode === 'accept'
        ? await mondayMovesApi.accept(row.id, { expected_holder: holder, note: note.trim() || undefined, idempotency_key: freshKey() })
        : await mondayMovesApi.reassign(row.id, { advocate_user_id: to, expected_from: holder, note: note.trim() || undefined, idempotency_key: freshKey() });
      if (!r.success) {
        const conflict = r.code === 'ASSIGNMENT_CONFLICT' || r.code === 'HOLDER_CHANGED';
        setError(conflict
          ? 'Someone placed this case since the list was loaded. Nothing was changed. Reload to see who holds it now.'
          : r.code === 'ALREADY_DECIDED' ? 'This move was already decided.' : (r.message || r.error || 'The server refused this decision.'));
        if (conflict || r.code === 'ALREADY_DECIDED') onDone('', true);
        return;
      }
      const who = advocates.find((a) => a.id === to);
      onDone(mode === 'accept'
        ? `${row.resident}: ${nameOr(row.holder_now)} kept as the baseline.`
        : `${row.resident}: reassigned to ${who ? who.name : 'the advocate you chose'}.`, true);
    } catch {
      setError('Could not reach the server. Nothing was changed.');
    } finally { setBusy(false); }
  };

  const choices = advocates.filter((a) => a.id !== holder);
  return (
    <article className="pa-mm-card" aria-label={`${row.resident}, ${CLASS_LABEL[row.classification]}`}>
      <header className="pa-mm-head">
        <div className="pa-mm-who">
          <strong>{row.resident}</strong>
          <span>
            {buildings.length > 1
              ? `${buildings.length} buildings on record for this resident; they disagree`
              : row.facility || buildings[0]?.name || 'No building on record'}
          </span>
        </div>
        <span className="pa-tags">
          <span className={`pa-tag ${tone(row.classification)}`}>{CLASS_LABEL[row.classification]}</span>
        </span>
      </header>
      <dl className="pa-kv">
        <dt>Monday moved it</dt>
        <dd>
          {nameOr(row.from)} → {nameOr(row.to)} on {shortDateTime(row.moved_at)}
          <span className="pa-mm-quiet"> · {KIND_LABEL[row.move_kind]} · {mondayUser(row, ev)}</span>
        </dd>
        <dt>Before the move</dt>
        <dd>{prior ? <>placed with {row.prior_assignment_to_name || 'nobody'} by {PLACEMENT_LABEL[prior.kind] || prior.kind} on {shortDateTime(prior.at)}</> : 'No earlier placement on record'}</dd>
        <dt>Where they live</dt>
        <dd>
          {decisionNote && <p className="pa-mm-note">{decisionNote}</p>}
          {buildings.length > 0 && (
            <div className="pa-mm-bld-wrap">
              <table className="pa-mm-bld" aria-label="Buildings on record and their PAs">
                <thead>
                  <tr><th scope="col">Building</th><th scope="col">On record in</th><th scope="col">PA at the move</th><th scope="col">PA now</th></tr>
                </thead>
                <tbody>
                  {buildings.map((b) => (
                    <tr key={b.facility_id} className={b.contradicts.length ? 'contra' : undefined}>
                      <th scope="row">
                        {b.name || 'A building not on record'}
                        {b.contradicts.length > 0 && <span className="pa-tag stop">contradicts</span>}
                      </th>
                      <td>{sourcesOf(b)}</td>
                      <td>{paCell(b.pa_at_move, b.contradicts.some((c) => c.when === 'AT_MOVE'))}</td>
                      <td>{paCell(b.pa_now, b.contradicts.some((c) => c.when === 'NOW'))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </dd>
        {rr && (rr.records.length > 1 || rr.inconsistencies.length > 0 || rr.unresolved) && (
          <><dt>Resident records</dt><dd><ResidentRecordsView rr={rr} /></dd></>
        )}
        <dt>Work since</dt>
        <dd>
          {nameOr(row.from, 'previous advocate')}: {activity(ev.activity?.fromAfter)}
          <br />
          {nameOr(row.to, 'new advocate')}: {activity(ev.activity?.toAfter)}
        </dd>
        {later && <><dt>Placed since</dt><dd>by {PLACEMENT_LABEL[later.kind] || later.kind} on {shortDateTime(later.at)}</dd></>}
        {ev.lastLaterMondayScope === 'AFTER_WINDOW' && ev.lastLaterPlacement && (
          <><dt>Moved again</dt><dd>by {PLACEMENT_LABEL[ev.lastLaterPlacement.kind] || ev.lastLaterPlacement.kind} on {shortDateTime(ev.lastLaterPlacement.at)}, after the review window</dd></>
        )}
        <dt>Holds it now</dt>
        <dd>{nameOr(row.holder_now)}{row.holder_now.stage ? ` · ${STAGE_LABEL[row.holder_now.stage as Stage] || row.holder_now.stage}` : ''}</dd>
        <dt>Why it is here</dt>
        <dd>
          <ul className="pa-mm-reasons">
            {row.reasons.map((r) => <li key={r}>{REASON_LABEL[r] || r}</li>)}
          </ul>
        </dd>
      </dl>

      {row.status !== 'OPEN' && (
        <p className="pa-mm-decided">
          {row.decision_basis === 'COVERED_BY_REASSIGN'
            ? 'Settled when the resident was reassigned from another row'
            : row.status === 'ACCEPTED' ? 'Accepted' : 'Reassigned'}
          {row.decided_by ? ` by ${nameOr(row.decided_by)}` : ''}{row.decided_at ? ` on ${shortDateTime(row.decided_at)}` : ''}
          {' — baseline: '}{nameOr(row.baseline)}{row.decision_note ? `. “${row.decision_note}”` : ''}
        </p>
      )}

      {row.status === 'OPEN' && mode === 'none' && (
        <div className="pa-mm-acts">
          <button type="button" className="pa-go" onClick={() => setMode('accept')}>Accept</button>
          {closed
            ? <span className="pa-mm-quiet">This case is closed, so it cannot be reassigned. Accept keeps {nameOr(row.holder_now)} as the baseline; reopen the case first to place it with someone else.</span>
            : <button type="button" className="pa-go ghost" onClick={() => setMode('reassign')}>Reassign</button>}
        </div>
      )}
      {row.status === 'OPEN' && mode !== 'none' && (
        <form className="pa-form" onSubmit={(e) => { e.preventDefault(); decide(); }}>
          {mode === 'accept'
            ? <p className="pa-confirm">Keep {nameOr(row.holder_now)} as this resident’s advocate. This is final: Monday can never move it again.</p>
            : (
              <label>
                <span>Reassign to</span>
                <select value={to} onChange={(e) => setTo(e.target.value)} aria-label="Reassign to">
                  <option value="">Choose an advocate…</option>
                  {choices.map((a) => <option key={a.id} value={a.id}>{a.name}{confirmedNow.has(a.id) ? ` · confirmed PA of ${confirmedNow.get(a.id)}` : ''}</option>)}
                </select>
              </label>
            )}
          <label>
            <span>Note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} aria-label="Note" />
          </label>
          {error && <p className="pa-error" role="alert">{error}</p>}
          <div className="btns">
            <button type="submit" className="pa-go" disabled={busy || (mode === 'reassign' && !to)}>
              {busy ? 'Recording…' : mode === 'accept' ? `Accept ${nameOr(row.holder_now)}` : 'Reassign'}
            </button>
            <button type="button" className="pa-go ghost" disabled={busy} onClick={() => { setMode('none'); setError(null); }}>Cancel</button>
          </div>
        </form>
      )}
    </article>
  );
}

export default function MondayMoveReview({ advocates }: { advocates: Advocate[] }) {
  const [cls, setCls] = useState<QueuedClass | null>(null);
  const [status, setStatus] = useState<StatusFilter>('OPEN');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MoveQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setFailure(null);
    try {
      const r = await mondayMovesApi.queue({ cls, status, page });
      if (r.success && r.data) setData(r.data);
      else setFailure(r.message || r.error || 'The review could not be read.');
    } catch { setFailure('Could not reach the server.'); } finally { setLoading(false); }
  }, [cls, status, page]);
  useEffect(() => { load(); }, [load]);

  const counts = data?.counts;
  const pick = (next: QueuedClass | null) => { setCls(next); setPage(1); };
  const n = (k: QueuedClass) => (counts ? (status === 'OPEN' ? counts.byClass[k].open : counts.byClass[k].decided) : null);
  const allN = counts ? (status === 'OPEN' ? counts.open : counts.decided) : null;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <section className="pa-mm" aria-label="Monday move review">
      <p className="pa-mm-lead">
        Monday moved these residents between advocates before its reassignment job was turned off on 5 Oct. Every move was checked;
        only the ones below need a decision.{counts ? ` ${counts.baseline} others were consistent or already settled in eMerge and were kept as they are.` : ''}
      </p>
      <div className="pa-lenses" role="group" aria-label="Decision">
        <button type="button" className={`pa-lens${status === 'OPEN' ? ' on' : ''}`} aria-pressed={status === 'OPEN'} onClick={() => { setStatus('OPEN'); setPage(1); }}>To decide</button>
        <button type="button" className={`pa-lens${status === 'DECIDED' ? ' on' : ''}`} aria-pressed={status === 'DECIDED'} onClick={() => { setStatus('DECIDED'); setPage(1); }}>Decided</button>
      </div>
      <div className="pa-lenses" role="group" aria-label="Class">
        <button type="button" className={`pa-lens${cls === null ? ' on' : ''}`} aria-pressed={cls === null} onClick={() => pick(null)}>
          All{allN !== null && <span className="pa-count">{allN}</span>}
        </button>
        {QUEUED_CLASSES.map((k) => (
          <button key={k} type="button" className={`pa-lens${cls === k ? ' on' : ''}`} aria-pressed={cls === k} title={CLASS_WHAT[k]} onClick={() => pick(k)}>
            {CLASS_LABEL[k]}{n(k) !== null && <span className="pa-count">{n(k)}</span>}
          </button>
        ))}
      </div>
      {cls && <p className="pa-mm-quiet">{CLASS_WHAT[cls]}</p>}
      {notice && <p className="pa-notice" role="status">{notice}</p>}
      {failure && <p className="pa-error" role="alert">{failure} <button type="button" className="pa-link" onClick={load}>Try again</button></p>}
      {!failure && loading && !data && <p className="pa-empty">Loading…</p>}
      {!failure && data && !data.available && <p className="pa-empty">The Monday move review is not set up on this server yet.</p>}
      {!failure && data && data.available && data.items.length === 0 && (
        <p className="pa-empty">{status === 'OPEN' ? 'Nothing left to decide here.' : 'No decisions yet.'}</p>
      )}
      {!failure && data && data.available && data.items.map((row) => (
        <MoveCard key={row.id} row={row} advocates={advocates} onDone={(msg, reload) => { if (msg) setNotice(msg); if (reload) load(); }} />
      ))}
      {data && data.available && data.total > data.pageSize && (
        <div className="pa-pager">
          <span>Page {page} of {pages} · {data.total} moves</span>
          <div className="btns">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      )}
    </section>
  );
}
